import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CalendarClock,
  Rocket,
  TrendingUp,
  Inbox,
  ArrowRight,
  AlertCircle,
} from "lucide-react";

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const ms = d.getTime() - now.getTime();
    return Math.round(ms / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
}

/**
 * RoleWidget — a role-aware overview tile that surfaces the single
 * most-important next action for the user based on their role.
 * Renders nothing when no data is available for the given role.
 */
export function RoleWidget({ widget }) {
  const { t } = useTranslation();
  if (!widget || !widget.role) return null;

  if (widget.role === "mentor") {
    const pending = widget.pending_requests || 0;
    const upcoming = widget.upcoming_confirmed || 0;
    const next = widget.next_session;
    const hasAvail = !!widget.has_availability;
    return (
      <Card
        className="rounded-2xl border border-border p-5 space-y-4"
        data-testid="dashboard-role-widget-mentor"
      >
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <CalendarClock className="h-4 w-4" />
          </span>
          <h3 className="font-display font-semibold">
            {t("dashboard.roleWidget.mentorTitle")}
          </h3>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div
            className="rounded-xl border border-border p-3"
            data-testid="dashboard-role-widget-mentor-pending"
          >
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {t("dashboard.roleWidget.mentorPending")}
            </p>
            <p className="font-display text-2xl font-semibold mt-1">{pending}</p>
          </div>
          <div
            className="rounded-xl border border-border p-3"
            data-testid="dashboard-role-widget-mentor-upcoming"
          >
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {t("dashboard.roleWidget.mentorUpcoming")}
            </p>
            <p className="font-display text-2xl font-semibold mt-1">{upcoming}</p>
          </div>
        </div>

        {next ? (
          <div
            className="rounded-xl border border-primary/30 bg-primary/5 p-3"
            data-testid="dashboard-role-widget-mentor-next"
          >
            <p className="text-[10px] uppercase tracking-widest text-primary font-semibold">
              {t("dashboard.roleWidget.mentorNextSession")}
            </p>
            <p className="text-sm font-medium mt-1 truncate">
              {t("dashboard.roleWidget.mentorWith")} {next.requester_name}
            </p>
            <p className="text-xs text-muted-foreground font-mono">
              {fmtDateTime(next.start_time)}
            </p>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            {t("dashboard.roleWidget.mentorNoUpcoming")}
          </p>
        )}

        {!hasAvail && (
          <div
            className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3 flex items-start gap-2"
            data-testid="dashboard-role-widget-mentor-avail-warning"
          >
            <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 dark:text-amber-400">
              {t("dashboard.roleWidget.mentorAvailabilityMissing")}
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Link to="/dashboard?tab=bookings">
            <Button
              size="sm"
              variant="outline"
              className="rounded-lg gap-1"
              data-testid="dashboard-role-widget-mentor-bookings-cta"
            >
              {t("dashboard.roleWidget.goBookings")}
              <ArrowRight className="h-3 w-3" />
            </Button>
          </Link>
          {!hasAvail && (
            <Link to="/dashboard?tab=availability">
              <Button
                size="sm"
                className="btn-brand rounded-lg gap-1"
                data-testid="dashboard-role-widget-mentor-avail-cta"
              >
                {t("dashboard.roleWidget.mentorSetAvailability")}
              </Button>
            </Link>
          )}
        </div>
      </Card>
    );
  }

  if (widget.role === "founder") {
    // Founder next-milestone widget removed per product decision — the
    // milestone details live in the Program view instead.
    return null;
  }

  if (widget.role === "investor") {
    return (
      <Card
        className="rounded-2xl border border-border p-5 space-y-4"
        data-testid="dashboard-role-widget-investor"
      >
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <TrendingUp className="h-4 w-4" />
          </span>
          <h3 className="font-display font-semibold">
            {t("dashboard.roleWidget.investorTitle")}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div
            className="rounded-xl border border-border p-3"
            data-testid="dashboard-role-widget-investor-companies"
          >
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {t("dashboard.roleWidget.investorCompanies")}
            </p>
            <p className="font-display text-2xl font-semibold mt-1">
              {widget.portfolio_companies || 0}
            </p>
          </div>
          <div
            className="rounded-xl border border-border p-3"
            data-testid="dashboard-role-widget-investor-rooms"
          >
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {t("dashboard.roleWidget.investorRooms")}
            </p>
            <p className="font-display text-2xl font-semibold mt-1">
              {widget.portfolio_rooms || 0}
            </p>
          </div>
          <div
            className="rounded-xl border border-border p-3"
            data-testid="dashboard-role-widget-investor-docs"
          >
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {t("dashboard.roleWidget.investorDocs")}
            </p>
            <p className="font-display text-2xl font-semibold mt-1">
              {widget.portfolio_docs || 0}
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return null;
}

export default RoleWidget;
